import { useRef, useState, useEffect } from "react";
import { Download, Printer, Share2, RefreshCw, Sparkles, Copy, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_ICONS = {
  business_overview: "🏪",
  target_customers: "👥",
  products_prices: "🍋",
  operations: "⚙️",
  money_plan: "💰",
  marketing: "📣",
  challenges: "🛠️",
  launch_day: "🚀",
  future_goals: "🌟",
  // Legacy section IDs (fallback)
  executive_summary: "💡",
  team_roles: "👥",
  branding_marketing: "🎨",
  pricing_money: "💰",
  pitch_vision: "🎯"
};

// Default placeholder logo (lemon icon as SVG data URL)
const PLACEHOLDER_LOGO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%23FCD34D'/%3E%3Cellipse cx='50' cy='50' rx='35' ry='40' fill='%23FBBF24'/%3E%3Cpath d='M50 15 Q55 5 60 15' stroke='%2316A34A' stroke-width='3' fill='none'/%3E%3Cellipse cx='55' cy='12' rx='8' ry='5' fill='%2322C55E'/%3E%3C/svg%3E";

function AIBusinessPlan({ plan, studentName, studentId, onRegenerate, canRegenerate, remainingRegenerations, isGenerating, onClose, showActions = true, canShare = false, hasDiscAssessment = false }) {
  const planRef = useRef(null);
  const { t } = useTranslation();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    loadLogo();
  }, [studentId]);

  const loadLogo = async () => {
    try {
      const endpoint = studentId ? `${API}/logo/${studentId}` : `${API}/logo/me`;
      const response = await axios.get(endpoint);
      if (response.data.has_logo) {
        setLogo(response.data.logo);
      }
    } catch (err) {
      console.log("No logo found");
    }
  };

  const handleDownloadPDF = async () => {
    if (!planRef.current) return;
    
    try {
      const canvas = await html2canvas(planRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement("a");
      link.download = `${studentName.replace(/\s+/g, "_")}_AI_Business_Plan.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to download. Please try again.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    setShowShareModal(true);
    setShareLoading(true);
    
    try {
      const statusRes = await axios.get(`${API}/business-plan/share/status`);
      
      if (statusRes.data.has_share_link) {
        const fullUrl = `${window.location.origin}/shared-plan/${statusRes.data.share_token}`;
        setShareLink({ url: fullUrl, isNew: false });
      } else {
        const createRes = await axios.post(`${API}/business-plan/share`);
        const fullUrl = `${window.location.origin}/shared-plan/${createRes.data.share_token}`;
        setShareLink({ url: fullUrl, isNew: true });
      }
    } catch (error) {
      console.error("Error creating share link:", error);
      alert("Failed to create share link. Please try again.");
      setShowShareModal(false);
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = shareLink.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!plan || !plan.sections) {
    return (
      <div className="text-center py-12">
        <Sparkles className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-pulse" />
        <p className="text-gray-600">Loading your AI-generated business plan...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Buttons */}
      {showActions && (
        <div className="flex flex-wrap gap-3 mb-6 print:hidden">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            data-testid="download-ai-plan-btn"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            data-testid="print-ai-plan-btn"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          {canShare && (
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              data-testid="share-ai-plan-btn"
            >
              <Share2 className="w-4 h-4" />
              Share with Family
            </button>
          )}
          {canRegenerate && remainingRegenerations > 0 && (
            <button
              onClick={onRegenerate}
              disabled={isGenerating}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50"
              data-testid="regenerate-ai-plan-btn"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? "Generating..." : `Regenerate (${remainingRegenerations} left)`}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium ml-auto"
              data-testid="close-ai-plan-btn"
            >
              Close
            </button>
          )}
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Share2 className="w-8 h-8" />
                  <h2 className="text-xl font-bold">Share Your AI Business Plan</h2>
                </div>
                <button onClick={() => setShowShareModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {shareLoading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-gray-600">Creating your share link...</p>
                </div>
              ) : shareLink ? (
                <>
                  <p className="text-gray-600 mb-4">
                    Share this link with family and friends!
                  </p>
                  <div className="bg-gray-100 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={shareLink.url}
                        readOnly
                        className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                      />
                      <button
                        onClick={handleCopyLink}
                        className={`p-2 rounded-lg transition-colors ${copied ? "bg-green-500 text-white" : "bg-white text-gray-600 hover:bg-gray-200"}`}
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {copied && (
                    <p className="text-green-600 text-sm mb-4 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Link copied!
                    </p>
                  )}
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                  >
                    Done
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* AI Business Plan Document */}
      <div
        ref={planRef}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        data-testid="ai-business-plan-document"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-8 text-6xl">🍋</div>
            <div className="absolute bottom-4 right-8 text-6xl">💼</div>
          </div>
          <div className="relative">
            {/* Logo Display */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center overflow-hidden border-4 border-white">
                <img
                  src={logo ? `data:image/png;base64,${logo.image_base64}` : PLACEHOLDER_LOGO}
                  alt="Business Logo"
                  className="w-full h-full object-contain"
                  data-testid="business-plan-logo"
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{textShadow: "1px 1px 2px rgba(0,0,0,0.2)"}}>
              My Lemonade Stand Business Plan
            </h1>
            <p className="text-white/90 text-lg font-semibold">
              {studentName}&apos;s Action Plan
            </p>
            <p className="text-white/80 text-sm mt-2">
              Ready to Launch!
            </p>
          </div>
        </div>

        {/* Action Plan Badge */}
        <div className="px-8 py-4 bg-green-50 border-b border-green-100">
          <div className="flex items-center justify-center gap-2">
            <span className="text-xl">🚀</span>
            <span className="font-semibold text-green-800">
              Your Step-by-Step Guide to Success
            </span>
          </div>
        </div>

        {/* Sections */}
        <div className="p-8 space-y-8">
          {plan.sections.map((section, index) => (
            <div key={section.id}>
              <div
                className="border-2 border-yellow-200 rounded-xl overflow-hidden bg-gradient-to-br from-white to-yellow-50/30"
                data-testid={`ai-plan-section-${section.id}`}
              >
                {/* Section Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-yellow-100 to-orange-100">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{section.icon || SECTION_ICONS[section.id] || "📄"}</span>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Section Content */}
                <div className="p-6">
                  <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap ai-plan-content">
                    {section.content}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Closing Statement */}
          {plan.closing_statement && (
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-6 border-2 border-green-300">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🎉</span>
                <h2 className="text-xl font-bold text-gray-800">You&apos;ve Got This!</h2>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                {plan.closing_statement}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-yellow-50 px-8 py-6 border-t border-yellow-100">
          <div className="text-center">
            <p className="text-gray-600 mb-2">
              Created by <strong>LemonBot AI Coach</strong> • Lemonade Stand Business Plan Program
            </p>
            <p className="text-sm text-gray-500">
              Your action plan to launch a successful lemonade stand!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIBusinessPlan;
